import {
  loadStripeTerminal,
  DiscoverResult,
  ErrorResponse,
  ISdkManagedPaymentIntent,
  Terminal
} from '@stripe/terminal-js';
import { AxiosError } from 'axios';
import get from 'lodash.get';
import { getErrorMessage } from 'services/api/normalizer';
import { HardwareResponse } from 'services/hardware/types';

class StripeModule {
  public terminal: Terminal | undefined;
  public connectionToken: string = '';
  public readerConnected: boolean = false;

  private initializeTerminal = async () => {
    const StripeTerminal = await loadStripeTerminal();

    if (StripeTerminal) {
      this.terminal = StripeTerminal.create({
        // eslint-disable-next-line require-await
        onFetchConnectionToken: async () => this.connectionToken,
        // eslint-disable-next-line require-await
        onUnexpectedReaderDisconnect: async () => {
          this.connectionToken = '';
          this.readerConnected = false;
        }
      });
    } else {
      throw new Error('StripeTerminal did not load');
    }

    if (!this.terminal) {
      throw new Error('terminal undefined in initializeTerminal()');
    }
  };

  private discoverReaders = async () => {
    if (this.terminal) {
      const discoverResult = await this.terminal.discoverReaders({ simulated: false });

      if ((discoverResult as DiscoverResult).discoveredReaders) {
        return (discoverResult as DiscoverResult).discoveredReaders;
      }

      throw new Error(get(discoverResult, 'error.message', 'No readers found'));
    } else {
      throw new Error('terminal undefined in discoverReaders()');
    }
  };

  private connectReader = async (readerId: string) => {
    if (this.terminal) {
      const discoveredReaders = await this.discoverReaders();
      const selectedReader = discoveredReaders.find((dr) => dr.id === readerId);

      if (selectedReader) {
        const connectResult = await this.terminal.connectReader(selectedReader);
        const readerStatus = get(connectResult, 'reader.status');

        if (!readerStatus) {
          throw new Error(`Could not connect to readerId: ${readerId}`);
        } else if (readerStatus === 'online') {
          this.readerConnected = true;
        } else {
          this.readerConnected = false;
          throw new Error(`Reader not online, status is: ${readerStatus}`);
        }
      } else {
        throw new Error(`Could not find readerId: ${readerId}`);
      }
    } else {
      throw new Error('terminal undefined in connectReader()');
    }
  };

  // eslint-disable-next-line class-methods-use-this
  private paymentIntentToHardwareResponseDTO = (paymentIntent: ISdkManagedPaymentIntent): HardwareResponse => {
    const chargeData = get(paymentIntent, 'charges.data[0]');
    const cardData: any = get(chargeData, 'payment_method_details.card_present', {});

    return {
      code: 'payment_success',
      message: {
        amount: (chargeData.amount / 100).toString(),
        cardNumber: cardData.last4,
        cardType: cardData.brand,
        cardholderName: cardData.cardholder_name,
        code: cardData.receipt && cardData.receipt.authorizationCode,
        currency: chargeData.currency,
        description: '',
        expiryDate: `${cardData.exp_month}${cardData.exp_year}`,
        orderId: chargeData.payment_intent as string,
        transactionDate: chargeData.created.toString(),
        voidParams: {}
      },
      raw_code: paymentIntent
    };
  };

  // eslint-disable-next-line class-methods-use-this
  private errorToHardwareResponseDTO = (err: any): HardwareResponse => {
    const responseData = { code: '', message: '', is_error: true, raw_code: err };

    // if code = 'card_declined' then decline_code provides the reason for the decline
    const errorCode = get(err, 'error.code', '');
    const errorDeclineCode = get(err, 'error.decline_code', errorCode);

    switch (errorDeclineCode) {
      case 'card_declined':
      case 'call_issuer':
      case 'generic_decline':
        responseData.code = 'card_declined';
        responseData.message = 'Card declined. Please try again or use a different card.';
        break;
      case 'incorrect_pin':
        responseData.code = 'pin_failed';
        responseData.message = 'PIN was invalid. Please try again.';
        break;
      case 'card_not_supported':
        responseData.code = 'unsupported_card_type';
        responseData.message = 'Card not supported. Please try a different card.';
        break;
      case 'test_mode_live_card':
        responseData.code = 'invalid_card';
        responseData.message = 'Invalid card. Please try a different card.';
        break;
      case 'canceled':
        responseData.code = 'canceled';
        responseData.message = 'Transaction cancelled';
        break;
      default:
        responseData.code = 'unknown';
        responseData.message = 'Something went wrong. Please go to the box office for assistance.';
        break;
    }

    return responseData;
  };

  // eslint-disable-next-line class-methods-use-this
  private generateAxiosError = (hardwareResponse: HardwareResponse): AxiosError => {
    return {
      config: {},
      isAxiosError: true,
      name: 'Stripe error',
      message: 'AxiosError generated in stripe.ts',
      response: {
        data: hardwareResponse,
        status: 400,
        statusText: 'Generic error',
        headers: {},
        config: {}
      },
      toJSON: () => hardwareResponse
    };
  };

  public pingReader = async (readerId: string) => {
    try {
      // initialize the terminal if it has not yet
      if (!this.terminal) await this.initializeTerminal();

      // connect to the reader if it has not yet
      if (!this.readerConnected) await this.connectReader(readerId);

      // return device_connected if no err was thrown at this point
      return {
        code: 'device_connected',
        message: 'Payment device connected',
        is_error: false
      };
    } catch (err) {
      // reset the connectionToken after a ping failure, because it expires
      this.connectionToken = '';

      // translate their error response to our HardwareResponse dto
      const pingResponse: HardwareResponse = {
        code: 'device_offline',
        message: 'Payment device offline',
        is_error: true,
        raw_code: getErrorMessage(err as any)
      };

      // return AxiosError to replicate responses from kis-local-server
      throw this.generateAxiosError(pingResponse);
    }
  };

  public collectAndProcessPayment = async (readerId: string, timeout: number, paymentIntentSecret: string) => {
    try {
      // initialize the terminal if it has not yet
      if (!this.terminal) await this.initializeTerminal();

      // connect to the reader if it has not yet
      if (!this.readerConnected) await this.connectReader(readerId);

      if (this.terminal) {
        // set a timeout (IF configured) to cancel the payment since the device does not timeout
        let timeoutId;
        if (timeout && timeout > 0) {
          timeoutId = setTimeout(async () => {
            await (this.terminal as Terminal).cancelCollectPaymentMethod();
          }, timeout * 1000);
        }

        // collect payment from the user
        const collectPaymentResponse = await this.terminal.collectPaymentMethod(paymentIntentSecret);
        // cancel the timeout set to cancel/timeout the payment
        if (timeoutId) clearTimeout(timeoutId);
        if ((collectPaymentResponse as ErrorResponse).error) throw collectPaymentResponse;

        // process the payment
        const processPaymentResponse = await this.terminal.processPayment(
          (collectPaymentResponse as { paymentIntent: ISdkManagedPaymentIntent }).paymentIntent
        );
        if ((processPaymentResponse as ErrorResponse).error) throw processPaymentResponse;

        // return the response as HardwareResponse if successful
        return this.paymentIntentToHardwareResponseDTO(
          (processPaymentResponse as { paymentIntent: any }).paymentIntent
        );
      }

      throw new Error('terminal undefined in collectAndProcessPayment()');
    } catch (err) {
      // translate their error response to our HardwareResponse dto
      const saleResponse = this.errorToHardwareResponseDTO(err);

      // return AxiosError to replicate responses from kis-local-server
      throw this.generateAxiosError(saleResponse);
    }
  };

  public cancelCollectPayment = async (readerId: string) => {
    try {
      // initialize the terminal if it has not yet
      if (!this.terminal) await this.initializeTerminal();

      // connect to the reader if it has not yet
      if (!this.readerConnected) await this.connectReader(readerId);

      if (this.terminal) {
        const cancelResponse = await this.terminal.cancelCollectPaymentMethod();
        if ((cancelResponse as ErrorResponse).error) throw cancelResponse;

        return {
          code: 'cancel_success',
          message: 'Transaction cancelled',
          is_error: false
        };
      }

      throw new Error('terminal undefined in cancelCollectPayment()');
    } catch (err) {
      // translate their error response to our HardwareResponse dto
      const cancelResponse: HardwareResponse = {
        code: 'cancel_failed',
        message: '',
        is_error: true,
        raw_code: getErrorMessage(err as any)
      };

      // return AxiosError to replicate responses from kis-local-server
      throw this.generateAxiosError(cancelResponse);
    }
  };
}

const stripeInstance = new StripeModule();

export default stripeInstance;
